import {
	ChangeTriggerKind,
	SearchInputForm,
	SearchInputTrigger,
	SearchPlaceholder,
	Select,
	SelectOption
} from "client/Components";
import { generateTrackingId } from "client/Misc/utils";
import { ReactElement, useEffect, useMemo } from "react";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import { Guid } from "client_legacy/ndaw/guid";
import { DocumentsViewType, SearchScope } from "client/Api/Documents/SharePoint/common";

const allSelectOptions: SelectOption<SearchScope>[] = [
	{
		value: SearchScope.CurrentFolder,
		text: localize("_CurrentFolder")
	},
	{
		value: SearchScope.Subfolders,
		text: localize("_CurrentAndSubfolders")
	}
];

export type SharePointFilter = {
	text: VersionedValue<string>;
	scope: SearchScope;
};

export type SharePointSearchFormProps = {
	value: SharePointFilter;
	onChange(value: SharePointFilter): void;
	viewKind: Exclude<DocumentsViewType, DocumentsViewType.RecycleBinView | DocumentsViewType.RecentlyModified>;
	isInRootFolder: boolean;
	isMobile: boolean;
};

export const SharePointSearchForm = (props: SharePointSearchFormProps): ReactElement => {
	const {
		value,
		onChange,
		viewKind,
		isInRootFolder,
		isMobile
	} = props;

	const { defaultSearchScope, supportedSearchScopes } = useMemo(
		(): ScopeConfiguration => {
			let result: ScopeConfiguration = getScopeConfiguration(viewKind, isInRootFolder);

			if (isMobile) {
				result = {
					defaultSearchScope: result.defaultSearchScope,
					supportedSearchScopes: [result.defaultSearchScope]
				};
			}

			return result;
		},
		[
			viewKind,
			isInRootFolder,
			isMobile
		]
	);

	const { text, scope } = value;

	useEffect(
		() => {
			if (supportedSearchScopes.includes(scope)) {
				return;
			}

			onChange({
				text,
				scope: defaultSearchScope
			});
		},
		[
			onChange,
			scope,
			text,
			defaultSearchScope,
			supportedSearchScopes,
		]
	);

	const selectOptions = useMemo(
		(): readonly SelectOption<SearchScope>[] => {
			return allSelectOptions.filter(
				x => supportedSearchScopes.includes(x.value)
			);
		},
		[supportedSearchScopes]
	);

	const searchInputTrigger = useMemo(
		(): SearchInputTrigger => {
			return {
				kind: ChangeTriggerKind.Debounced,
				debounceInMs: 500
			};
		},
		[]
	);

	const textChangeHandler = (value: string, version: Guid): void => {
		onChange({
			text: {
				value,
				version
			},
			scope
		});
	};

	const scopeChangeHandler = (value: SearchScope): void => {
		onChange({
			text: text,
			scope: value
		});
	};

	const trackingId = "sharepoint-search-term";

	return (
		<SearchInputForm
			trackingId={trackingId}
			value={text}
			onChange={textChangeHandler}
			trigger={searchInputTrigger}
			placeholder={
				<SearchPlaceholder
					kind="search"
					properties={[localize("_Name")]}
				/>
			}
			height="large"
			addon={(
				selectOptions.length > 1
					? (
						<Select
							trackingId={generateTrackingId(trackingId, "__scope")}
							value={scope}
							onChange={scopeChangeHandler}
							options={selectOptions}
							menuTitle={localize("_SearchIn")}
							height="large"
						/>
					)
					: undefined
			)}
		/>
	);
};
SharePointSearchForm.displayName = "SharePointSearchForm";

type ScopeConfiguration = {
	defaultSearchScope: SearchScope;
	supportedSearchScopes: readonly SearchScope[];
};

function getScopeConfiguration(
	viewKind: Exclude<DocumentsViewType, DocumentsViewType.RecycleBinView | DocumentsViewType.RecentlyModified>,
	isInRootFolder: boolean
): ScopeConfiguration {
	switch (viewKind) {
		case DocumentsViewType.EditableView:
			return {
				defaultSearchScope: SearchScope.Subfolders,
				supportedSearchScopes: [SearchScope.Subfolders, SearchScope.CurrentFolder]
			};
		// We cannot search inside sub folders when already in a search-based views like shared with me.
		// However, if a user opened a folder from such a view, it becomes possible
		case DocumentsViewType.SharedWithMeView:
			if (isInRootFolder) {
				return {
					defaultSearchScope: SearchScope.CurrentFolder,
					supportedSearchScopes: [SearchScope.CurrentFolder]
				};
			} else {
				return {
					defaultSearchScope: SearchScope.Subfolders,
					supportedSearchScopes: [SearchScope.Subfolders, SearchScope.CurrentFolder]
				};
			}
		default:
			never(viewKind);
	}
}